import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

/**
 * BoxBreathingVisual - Reusable Box Breathing Component
 * 
 * A calming 4-4-4-4 breathing exercise with visual guidance.
 * Inhale 4s → Hold 4s → Exhale 4s → Hold 4s
 */

const PHASE_DURATION = 4000; // 4 seconds per phase

const phases = [
    { name: 'Inhale', instruction: 'Breathe in slowly...', scale: 1.5 },
    { name: 'Hold', instruction: 'Hold gently...', scale: 1.5 },
    { name: 'Exhale', instruction: 'Release slowly...', scale: 1 },
    { name: 'Hold', instruction: 'Rest here...', scale: 1 }
];

export const BoxBreathingVisual = ({
    autoStart = true,
    size = 'md', // 'sm' | 'md' | 'lg'
    variant = 'sage', // 'sage' | 'blue' | 'lavender'
    showControls = false,
    showPhaseIndicators = true,
    className,
    onPhaseChange,
}) => {
    const [isRunning, setIsRunning] = useState(autoStart);
    const [currentPhase, setCurrentPhase] = useState(0);
    const [progress, setProgress] = useState(0);
    const [cycleCount, setCycleCount] = useState(0);

    const phase = phases[currentPhase];

    // Size configurations
    const sizes = {
        sm: { container: 'w-32 h-32', box: 'w-16 h-16', text: 'text-2xl', label: 'text-sm' },
        md: { container: 'w-48 h-48', box: 'w-24 h-24', text: 'text-3xl', label: 'text-base' },
        lg: { container: 'w-64 h-64', box: 'w-32 h-32', text: 'text-4xl', label: 'text-lg' },
    };

    // Variant colors
    const variants = {
        sage: {
            expandGradient: 'linear-gradient(135deg, #7D8E95 0%, #8FB89F 100%)',
            contractGradient: 'linear-gradient(135deg, #7D8E95 0%, #7D8E95 100%)',
            expandShadow: 'rgba(52,70,72,0.3)',
            contractShadow: 'rgba(125,142,149,0.3)',
            indicator: 'bg-[#1D6B6B]',
            indicatorDone: 'bg-[#7D8E95]',
        },
        blue: {
            expandGradient: 'linear-gradient(135deg, #7D8E95 0%, #8BAFC8 100%)',
            contractGradient: 'linear-gradient(135deg, #D8E8F0 0%, #B8D0E0 100%)',
            expandShadow: 'rgba(125,142,149,0.3)',
            contractShadow: 'rgba(150,170,180,0.3)',
            indicator: 'bg-[#7D8E95]',
            indicatorDone: 'bg-[#7D8E95]',
        },
        lavender: {
            expandGradient: 'linear-gradient(135deg, #C8B8D8 0%, #B8A8C8 100%)',
            contractGradient: 'linear-gradient(135deg, #E8E0F0 0%, #D8D0E8 100%)',
            expandShadow: 'rgba(150,130,170,0.3)',
            contractShadow: 'rgba(170,160,180,0.3)',
            indicator: 'bg-[#9A8AAA]',
            indicatorDone: 'bg-[#C8B8D8]',
        },
    };

    const s = sizes[size];
    const v = variants[variant];

    // Progress timer
    useEffect(() => {
        if (!isRunning) return;

        const startTime = Date.now();
        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const phaseProgress = Math.min(elapsed / PHASE_DURATION, 1);
            setProgress(phaseProgress);

            if (elapsed >= PHASE_DURATION) {
                // Move to next phase
                setCurrentPhase(prev => {
                    const next = (prev + 1) % 4;
                    if (next === 0) {
                        setCycleCount(c => c + 1);
                    }
                    onPhaseChange?.(phases[next].name.toLowerCase());
                    return next;
                });
                setProgress(0);
            }
        }, 50);

        return () => clearInterval(interval);
    }, [isRunning, currentPhase, onPhaseChange]);

    // Calculate current scale based on phase and progress
    const getScale = () => {
        if (currentPhase === 0) {
            // Inhale: grow from 1 to 1.5
            return 1 + (0.5 * progress);
        } else if (currentPhase === 2) {
            // Exhale: shrink from 1.5 to 1
            return 1.5 - (0.5 * progress);
        }
        // Hold phases: stay at current scale
        return phase.scale;
    };

    const scale = getScale();
    const countdown = Math.ceil(PHASE_DURATION / 1000 - (progress * PHASE_DURATION / 1000));
    const isExpanded = currentPhase === 0 || currentPhase === 1;

    return (
        <div className={cn('flex flex-col items-center', className)}>
            {/* Breathing Box */}
            <div className={cn('relative flex items-center justify-center mb-6', s.container)}>
                {/* Outer guide box */}
                <div 
                    className="absolute inset-0 rounded-3xl border-2 border-dashed border-[#FFBB98] opacity-40"
                    style={{ transform: 'scale(1.5)' }}
                />
                
                {/* Animated breathing box */}
                <div
                    className={cn('rounded-3xl transition-colors duration-500', s.box)}
                    style={{
                        transform: `scale(${scale})`,
                        transition: isRunning ? 'transform 0.1s linear' : 'transform 0.5s ease-out',
                        background: isExpanded ? v.expandGradient : v.contractGradient,
                        boxShadow: `0 8px 32px ${isExpanded ? v.expandShadow : v.contractShadow}`
                    }}
                />

                {/* Center content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center z-10">
                    {isRunning ? (
                        <>
                            <span className={cn('font-light text-white mb-1', s.text)}>
                                {countdown}
                            </span>
                            <span className={cn('font-medium text-white/90', s.label)}>
                                {phase.name}
                            </span>
                        </>
                    ) : (
                        <button 
                            onClick={() => setIsRunning(true)}
                            className={cn('font-medium text-white/80 hover:text-white transition-colors', s.label)}
                        >
                            Tap to start
                        </button>
                    )}
                </div>
            </div>

            {/* Instruction */}
            <div className="text-center mb-4 h-8">
                {isRunning && (
                    <p className="text-[#7D8E95] font-['Lora'] animate-fade-in">
                        {phase.instruction}
                    </p>
                )}
            </div>

            {/* Phase indicators */}
            {showPhaseIndicators && (
                <div className="flex items-center gap-2">
                    {phases.map((p, index) => (
                        <div
                            key={index}
                            className={cn(
                                'w-2 h-2 rounded-full transition-all duration-300',
                                index === currentPhase && isRunning
                                    ? cn(v.indicator, 'scale-125')
                                    : index < currentPhase || (currentPhase === 0 && index === 3 && cycleCount > 0)
                                        ? v.indicatorDone
                                        : 'bg-[#FFBB98]'
                            )}
                        />
                    ))}
                </div>
            )}

            {/* Controls */}
            {showControls && (
                <div className="flex items-center gap-3 mt-4">
                    <button
                        onClick={() => setIsRunning(!isRunning)}
                        className="px-4 py-2 rounded-full bg-[#FFBB98]/50 text-[#1D6B6B] text-sm hover:bg-[#FFBB98] transition-colors"
                    >
                        {isRunning ? 'Pause' : 'Resume'}
                    </button>
                    <button
                        onClick={() => {
                            setIsRunning(false);
                            setCurrentPhase(0);
                            setProgress(0);
                            setCycleCount(0);
                        }}
                        className="px-4 py-2 rounded-full bg-[#FFBB98]/30 text-[#7D8E95] text-sm hover:bg-[#FFBB98]/50 transition-colors"
                    >
                        Reset
                    </button>
                </div>
            )}
        </div>
    );
};

export default BoxBreathingVisual;
